import { StyleSheet, View, ViewProps } from 'react-native'
import { InfoBlock } from '@dms/component'
import { Trans, useTranslation } from 'react-i18next'
import { Comment, Photo, usePhotos, useComments } from 'zens-store'
import { Button, Color, Icon, Hyperlink, Spacing, Table, Text } from 'zens-storybook'
import { Link } from 'zens-router'

const styles = StyleSheet.create({
  container: {},
  photo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  photoText: {
    marginRight: Spacing.XS,
    color: Color.metalGrey,
  },
  viewAll: {
    alignSelf: 'flex-end',
    marginTop: Spacing.S,
  },
  empty: {
    color: Color.metalGrey,
  },
})

interface Props extends ViewProps {
  companyId: number
  locationId?: number
  workspaceId: number
}

const WorkspaceFeedback = (props: Props) => {
  const { companyId, locationId, workspaceId, style, ...otherProps } = props
  const { t } = useTranslation()

  const photos = usePhotos({ companyId, workspaceId })
  const comments = useComments({ companyId, workspaceId })

  const rows = [...(photos.data || []), ...(comments.data || [])]
    .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0))
    .slice(0, 3)
    .map((item) => ({
      data: item,
      href: {
        pathname: `/companies/${companyId}/feedback`,
        params: {
          locationId,
          workspaceId,
          ...(item instanceof Comment ? { commentId: item.id } : { photoId: item.id }),
        },
      },
    }))

  const cols = [
    {
      key: 'text',
      title: t('Message'),
      render: (item: Comment | Photo) =>
        item instanceof Comment ? (
          <Text numberOfLines={1}>{item.text}</Text>
        ) : (
          <View style={styles.photo}>
            <Text style={styles.photoText}>
              <Trans>Photo attached</Trans>
            </Text>
            <Icon name="paperclip" size={20} />
          </View>
        ),
      size: { flex: 2 },
      sort: false as const,
    },
    {
      key: 'createdAt',
      title: t('Date'),
      render: (item: Comment | Photo) => <Text>{item.displayCreatedAt}</Text>,
      size: { flex: 1 },
      sort: false as const,
    },
  ]

  return (
    <InfoBlock
      style={[styles.container, style]}
      title={{
        text: `${t('Remarks')} (${(photos.data?.length || 0) + (comments.data?.length || 0)})`,
        ...(locationId
          ? {
              right: (
                <Link
                  href={{ pathname: `/companies/${companyId}/feedback/add`, params: { locationId, workspaceId } }}
                  asChild
                >
                  <Button title={t('Add remark')} left="plus" testID="WorkspaceFeedback.Add" />
                </Link>
              ),
            }
          : undefined),
      }}
      {...otherProps}
    >
      <Table
        rows={rows}
        dataIsSorted
        cols={cols}
        ListEmptyComponent={<Text style={styles.empty}>{t('No remarks yet.')}</Text>}
        ListFooterComponent={
          rows.length > 0 ? (
            <Link
              href={{
                pathname: `/companies/${companyId}/feedback`,
                params: { locationId, workspaceId },
              }}
              asChild
            >
              <Hyperlink style={styles.viewAll} title={t('View all remarks')} testID="WorkspaceFeedback.ViewAll" />
            </Link>
          ) : undefined
        }
      />
    </InfoBlock>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <InfoBlock.Skeleton
      style={[styles.container, style]}
      title={t('Remarks')}
      right={<Button.Skeleton />}
      {...otherProps}
    />
  )
}
WorkspaceFeedback.Skeleton = Skeleton

export default WorkspaceFeedback
